import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { linksDb } from '../../lib/db/index.js';
import { useAuth } from '../../lib/firebase/auth-context.js';
import { linkInputSchema } from '../../lib/schemas/link.js';
import {
  IMPORTANCE_VALUES,
  type Importance,
  type SourceType,
} from '../../types/link.js';
import type { Visibility } from '../../types/visibility.js';
import { detectSourceType, extractDomain, extractYoutubeVideoId, youtubeThumbnailUrl } from '../../lib/utils/url.js';
import { fetchOg, isOgEmpty, OgFetchError } from '../../lib/utils/og.js';
import { toAppError } from '../../lib/utils/error.js';
import { Spinner } from '../shared/Spinner.js';
import { TagInput } from './TagInput.js';
import { VisibilityRadio } from './VisibilityRadio.js';
import { useDraft } from './useDraft.js';
import { DraftRestoredBanner } from './DraftRestoredBanner.js';
import { ProjectSelectField } from './ProjectSelectField.js';

export interface LinkDraftState {
  url: string;
  title: string;
  summary: string;
  userComment: string;
  importance: Importance;
  tags: string[];
  thumbnailUrl: string;
  channelName: string;
  visibility: Visibility;
  projectId?: string | undefined;
}

const INITIAL: LinkDraftState = {
  url: '',
  title: '',
  summary: '',
  userComment: '',
  importance: '中',
  tags: [],
  thumbnailUrl: '',
  channelName: '',
  visibility: 'private',
};

interface LinkFormProps {
  onDone: (id: string) => void;
  /** 編集モードのとき対象 doc の ID */
  editId?: string | undefined;
  /** 編集モードのとき初期値（fetched doc 由来） */
  initialValues?: LinkDraftState | undefined;
}

export function LinkForm({ onDone, editId, initialValues }: LinkFormProps) {
  const isEditing = !!editId;
  const { fbUser } = useAuth();
  const qc = useQueryClient();

  // 編集モード: useDraft をスキップして plain useState（localStorage 汚染回避）
  // 新規モード: 既存の useDraft（500ms debounce で localStorage 自動保存）
  const draftHook = useDraft<LinkDraftState>('link', INITIAL);
  const [editDraft, setEditDraft] = useState<LinkDraftState>(initialValues ?? INITIAL);
  const draft = isEditing ? editDraft : draftHook.value;
  const setDraft = isEditing ? setEditDraft : draftHook.set;
  const clearDraft = isEditing ? () => {} : draftHook.clear;
  const wasRestored = isEditing ? false : draftHook.wasRestored;

  const [error, setError] = useState<string | null>(null);
  const [metaInfo, setMetaInfo] = useState<string | null>(null);
  const [fetching, setFetching] = useState(false);

  const set = <K extends keyof LinkDraftState>(k: K, v: LinkDraftState[K]) =>
    setDraft({ ...draft, [k]: v });

  const onUrlBlur = async () => {
    if (!draft.url) return;
    const ytId = extractYoutubeVideoId(draft.url);
    if (ytId && !draft.thumbnailUrl) {
      set('thumbnailUrl', youtubeThumbnailUrl(ytId));
    }
  };

  const fetchMeta = async () => {
    if (!draft.url) return;
    setFetching(true);
    setMetaInfo(null);
    setError(null);
    try {
      const og = await fetchOg(draft.url);
      if (isOgEmpty(og)) {
        setMetaInfo(
          'メタ情報が取得できませんでした。サイトが非ブラウザからのアクセスを制限しているか、未公開の可能性があります。タイトル等は手動で入力してください。',
        );
        return;
      }
      // 複数フィールドを一度に更新する。set() を連続で呼ぶと毎回 draft の
      // クロージャ値からマージするため、最後の set 以外が失われる（React の
      // setState は関数版でない限りバッチで上書きする）。
      const patch: Partial<LinkDraftState> = {};
      const filled: string[] = [];
      const missing: string[] = [];
      if (og.title && !draft.title) {
        patch.title = og.title;
        filled.push('タイトル');
      } else if (!og.title) {
        missing.push('タイトル');
      }
      if (og.description && !draft.summary) {
        patch.summary = og.description;
        filled.push('概要');
      } else if (!og.description) {
        missing.push('概要');
      }
      if (og.image && !draft.thumbnailUrl) {
        patch.thumbnailUrl = og.image;
        filled.push('サムネイル');
      }
      // channelName は missing には載せない（必須ではない・ノイズ抑制）
      if (og.channelName && !draft.channelName) {
        patch.channelName = og.channelName;
        filled.push('チャンネル');
      }
      if (Object.keys(patch).length > 0) {
        setDraft({ ...draft, ...patch } as LinkDraftState);
      }
      if (filled.length > 0) {
        setMetaInfo(
          missing.length > 0
            ? `取得しました: ${filled.join(' / ')}（${missing.join('・')} は取得できなかったため手動入力してください）`
            : `取得しました: ${filled.join(' / ')}`,
        );
      } else if (missing.length > 0) {
        setMetaInfo(
          `${missing.join('・')} は取得できませんでした。サイトが非ブラウザからのアクセスを制限している可能性があります。手動で入力してください。`,
        );
      } else {
        setMetaInfo('既に入力済のため自動入力はスキップしました（取得自体は成功）');
      }
    } catch (e: unknown) {
      const msg = e instanceof OgFetchError ? e.message : '取得に失敗しました';
      setError(`メタ取得エラー: ${msg}`);
    } finally {
      setFetching(false);
    }
  };

  const m = useMutation({
    mutationFn: async () => {
      if (!fbUser) throw new Error('not signed in');
      // 編集モードで URL が変更された場合、sourceType / domain も自動的に再計算して
      // 反映する（ユーザに UI で意識させないが、Zod が不正 URL なら parse でエラー）
      const sourceType: SourceType = detectSourceType(draft.url);
      const parsed = linkInputSchema.parse({
        title: draft.title,
        url: draft.url,
        sourceType,
        domain: extractDomain(draft.url),
        summary: draft.summary,
        userComment: draft.userComment,
        importance: draft.importance,
        tags: draft.tags,
        visibility: draft.visibility,
        thumbnailUrl: draft.thumbnailUrl || undefined,
        channelName: draft.channelName.trim() || undefined,
        projectId: draft.projectId,
      });
      const payload = {
        title: parsed.title,
        url: parsed.url,
        sourceType: parsed.sourceType as SourceType,
        domain: parsed.domain,
        summary: parsed.summary,
        userComment: parsed.userComment,
        importance: parsed.importance as Importance,
        tags: parsed.tags,
        visibility: parsed.visibility as Visibility,
        thumbnailUrl: parsed.thumbnailUrl,
        channelName: parsed.channelName,
        projectId: parsed.projectId,
      };
      if (editId) {
        await linksDb.update(editId, payload);
        return editId;
      }
      return await linksDb.create(fbUser.uid, payload);
    },
    onSuccess: async (id) => {
      clearDraft();
      await qc.invalidateQueries({ queryKey: ['links'] });
      await qc.invalidateQueries({ queryKey: ['home', 'recentLinks'] });
      // プロジェクト紐付けがある（or 紐付けが変わる可能性）→ projects 系キャッシュを更新
      // ProjectsPage の集計カウント / ProjectDetailPage の関連投稿一覧 / project キャッシュ
      await qc.invalidateQueries({ queryKey: ['projects', 'counts'] });
      await qc.invalidateQueries({ queryKey: ['project'] });
      // 編集モードの場合は詳細ページの個別キャッシュも refetch して
      // 即座に最新データで描画（同じ URL に navigate するので invalidate だけでは
      // refetch 完了前に古い描画が見える）
      if (editId) {
        await qc.refetchQueries({ queryKey: ['link', editId] });
      }
      onDone(id);
    },
    onError: (err) => setError(toAppError(err).userMessage),
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    m.mutate();
  };

  return (
    <form onSubmit={onSubmit} className="compose-form">
      {!isEditing && (
        <DraftRestoredBanner visible={wasRestored} onDiscard={clearDraft} />
      )}
      <label className="auth-field">
        <span>URL</span>
        <input
          type="url"
          required
          value={draft.url}
          onChange={(e) => set('url', e.target.value)}
          onBlur={onUrlBlur}
          placeholder="https://..."
        />
        <button
          type="button"
          className="btn btn-ghost mono"
          onClick={fetchMeta}
          disabled={fetching || !draft.url}
        >
          {fetching ? <Spinner /> : 'メタ取得'}
        </button>
        {metaInfo && (
          <div
            className="mono"
            style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 4 }}
          >
            {metaInfo}
          </div>
        )}
      </label>

      {draft.thumbnailUrl && (
        <div className="auth-field">
          <span>サムネイル</span>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 12,
              padding: 8,
              border: '1px solid var(--line)',
              borderRadius: 8,
              background: 'var(--bg-2)',
            }}
          >
            <img
              src={draft.thumbnailUrl}
              alt="thumbnail"
              loading="lazy"
              style={{
                width: 160,
                height: 90,
                objectFit: 'cover',
                borderRadius: 6,
                border: '1px solid var(--line)',
                flexShrink: 0,
                background: 'var(--bg-raised)',
              }}
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = 'none';
              }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                className="mono"
                style={{
                  fontSize: 10,
                  color: 'var(--ink-4)',
                  wordBreak: 'break-all',
                  marginBottom: 6,
                }}
              >
                {draft.thumbnailUrl}
              </div>
              <button
                type="button"
                className="btn ghost xs"
                onClick={() => set('thumbnailUrl', '')}
              >
                クリア
              </button>
            </div>
          </div>
        </div>
      )}

      <label className="auth-field">
        <span>タイトル</span>
        <input
          required
          value={draft.title}
          onChange={(e) => set('title', e.target.value)}
          maxLength={200}
        />
      </label>

      <label className="auth-field">
        <span>チャンネル名 <em className="mono">任意 / YouTube 等で自動入力</em></span>
        <input
          value={draft.channelName}
          onChange={(e) => set('channelName', e.target.value)}
          maxLength={256}
          placeholder="例: チャンネル名"
        />
      </label>

      <label className="auth-field">
        <span>概要 <em className="mono">任意</em></span>
        <textarea
          rows={2}
          value={draft.summary}
          onChange={(e) => set('summary', e.target.value)}
          maxLength={2000}
        />
      </label>

      <label className="auth-field">
        <span>共有コメント <em className="mono">なぜ参考になるか</em></span>
        <textarea
          rows={3}
          value={draft.userComment}
          onChange={(e) => set('userComment', e.target.value)}
          maxLength={2000}
        />
      </label>

      <label className="auth-field">
        <span>重要度</span>
        <select
          value={draft.importance}
          onChange={(e) => set('importance', e.target.value as Importance)}
        >
          {IMPORTANCE_VALUES.map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </select>
      </label>

      <div className="auth-field">
        <span>タグ</span>
        <TagInput value={draft.tags} onChange={(t) => set('tags', t)} />
      </div>

      <ProjectSelectField
        value={draft.projectId}
        onChange={(v) => set('projectId', v)}
      />

      <VisibilityRadio
        value={draft.visibility}
        onChange={(v) => set('visibility', v)}
      />

      {error && <div className="auth-error">{error}</div>}

      <div className="compose-foot">
        <span className="mono compose-draft-hint">
          {isEditing ? '編集モード' : '下書きは自動保存されます'}
        </span>
        <button type="submit" className="btn btn-primary" disabled={m.isPending}>
          {m.isPending ? <Spinner /> : isEditing ? '更新' : '登録'}
        </button>
      </div>
    </form>
  );
}
